import { COOKIES_NAME_CONFIG } from "@/config";
import { CSRF_ENDPOINT_INSIDE } from "@/config/endpoints/insideEndpoints";

function getCookie(name: string): string | null {
  if (typeof document === "undefined") return null;
  const match = document.cookie
    .split(";")
    .map((c) => c.trim())
    .find((c) => c.startsWith(`${name}=`));
  if (!match) return null;
  return match.split("=").slice(1).join("=");
}

async function ensureCsrfToken(): Promise<string | null> {
  // Check cookie first
  let token = getCookie(COOKIES_NAME_CONFIG.XSRF_TOKEN);
  if (token) return token;

  // Fetch CSRF endpoint to set the cookie and get token
  try {
    const res = await fetch(CSRF_ENDPOINT_INSIDE, { credentials: "include" });
    if (!res.ok) return null;
    const json = await res.json();
    token =
      json?.csrfToken || getCookie(COOKIES_NAME_CONFIG.XSRF_TOKEN) || null;
    return token;
  } catch (e) {
    console.warn("[fetchWithXSRF] CSRF fetch failed", e);
    return null;
  }
}

export interface FetchWithXSRFOptions {
  body?: unknown;
  headers?: HeadersInit;
  method?: string;
}

export async function fetchWithXSRF(
  input: RequestInfo,
  { body, headers, ...init }: FetchWithXSRFOptions = {},
): Promise<Response> {
  if (typeof window === "undefined") {
    throw new Error("fetchWithXSRF must be used in browser context");
  }

  const token = await ensureCsrfToken();
  const isFormData = body instanceof FormData;

  const h = new Headers(headers || {});
  if (token) h.set("x-xsrf-token", token);
  // Don't set Content-Type for FormData — browser sets it with boundary
  if (!isFormData && !h.has("Content-Type") && body !== undefined)
    h.set("Content-Type", "application/json");

  const res = await fetch(input, {
    ...init,
    credentials: "include",
    headers: h,
    body: isFormData
      ? (body as FormData)
      : body !== undefined && typeof body !== "string"
        ? JSON.stringify(body)
        : (body as any),
  });

  return res;
}

export default fetchWithXSRF;
